import useSWR from "swr"
import { useMemo } from "preact/hooks"
import { api_fetch, api_fetch_throws } from "./client.tsx"
import {
  getDefaultCodingWizPreferences,
  type CodingWizPreferences,
} from "@/accounting/coding_wiz_preferences"

/**
 * The team's CodingWiz defaults live on the organization's accounting auto-code settings
 * (`organization.accounting_auto_code_settings.coding_wiz_preferences`), so a bulk run started
 * from any browser uses the same options. They used to sit in each browser's localStorage.
 */
interface AutoCodeSettingsWithPreferences {
  coding_wiz_preferences: CodingWizPreferences | null
  [key: string]: unknown
}

export const CODING_WIZ_PREFERENCES_KEY = "accounting-auto-code-settings"

async function fetch_settings(): Promise<AutoCodeSettingsWithPreferences> {
  return api_fetch_throws<AutoCodeSettingsWithPreferences>("/organization/accounting-auto-code-settings")
}

/** Merge new preferences into the org's settings without disturbing the other fields. */
export async function save_coding_wiz_preferences(prefs: CodingWizPreferences): Promise<boolean> {
  const current = await fetch_settings()
  const res = await api_fetch<AutoCodeSettingsWithPreferences>("/organization/accounting-auto-code-settings", {
    method: "PUT",
    body: { ...current, coding_wiz_preferences: prefs },
  })
  return res.ok
}

/** The org's CodingWiz defaults (built-in defaults until the team saves some). */
export function use_coding_wiz_preferences(): {
  prefs: CodingWizPreferences
  saved: boolean
  loading: boolean
  save: (prefs: CodingWizPreferences) => Promise<boolean>
} {
  const { data, isLoading, mutate } = useSWR(CODING_WIZ_PREFERENCES_KEY, fetch_settings)
  const saved = data?.coding_wiz_preferences ?? null
  // Callers list `prefs` as an effect dependency, so the fallback must be the same object on
  // every render — a fresh default each time looped CodingWizSheet and aborted every run.
  const prefs = useMemo(() => saved ?? getDefaultCodingWizPreferences(), [saved])
  async function save(prefs: CodingWizPreferences): Promise<boolean> {
    const ok = await save_coding_wiz_preferences(prefs)
    if (ok) {
      await mutate()
    }
    return ok
  }
  return { prefs, saved: saved !== null, loading: isLoading, save }
}
